/**
 * Global axios interceptor that:
 *  - Auto-attaches the latest Supabase access_token to every request
 *  - On 401, refreshes the Supabase session and retries the request once
 *  - On unrecoverable 401, signs the user out and bounces to /login
 */
import axios from "axios";
import { supabase, syncTokenFromSession } from "@/lib/supabaseClient";

let refreshPromise = null;
let installed = false;

async function getFreshToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data?.session) return null;
        syncTokenFromSession(data.session);
        return data.session.access_token;
      } finally {
        setTimeout(() => { refreshPromise = null; }, 100);
      }
    })();
  }
  return refreshPromise;
}

export function installAxiosInterceptors() {
  if (installed) return;
  installed = true;

  // Hard cap per request so a hung socket can't keep promises pending forever.
  if (!axios.defaults.timeout) {
    axios.defaults.timeout = 30000;
  }

  axios.interceptors.request.use((config) => {
    const url = config.url || "";
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const isOurs = url.startsWith("/api") || (backend && url.startsWith(backend));
    if (!isOurs) return config;
    const tok = localStorage.getItem("token");
    if (tok) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${tok}`;
      }
    }
    const impId = localStorage.getItem("impersonate_id");
    if (impId) {
      config.headers = config.headers || {};
      config.headers["X-Impersonate-User"] = impId;
    }
    return config;
  });

  axios.interceptors.response.use(
    (r) => r,
    async (err) => {
      const original = err?.config;
      const status = err?.response?.status;
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        const fresh = await getFreshToken();
        if (fresh) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${fresh}`;
          return axios(original);
        }
        try { await supabase.auth.signOut(); } catch (_) {}
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
      }
      return Promise.reject(err);
    }
  );
}
